import { useCallback, useEffect, useRef, useState } from "react";
import { getClientsPaged } from "../service/client.service";
import type { TClientForm, TClientScope } from "../types/index.type";

const MIN_NAME_CHARS = 2;
const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_PAGE_SIZE = 25;

// MRN / DOB fragments are useful even at 1 char, so the min-char gate is for NAME searches only.
export const looksNumericOrDate = (q: string) => /[0-9/]/.test(q);

export type UseClientSearch = {
  query: string;
  results: TClientForm[];
  loading: boolean;
  error: boolean;
  /** name search below the min-char threshold (and not an MRN/DOB-looking query). */
  belowMinChars: boolean;
  onSearch: (raw: string) => void;
  retry: () => void;
  reset: () => void;
};

/**
 * Shared server-search logic for every client picker: debounced queries, stale-response drop via
 * AbortController (so an out-of-order response can't repaint the wrong list), a name-only min-char
 * gate, and an explicit `error` state distinct from "no results". Each picker renders these states
 * its own way; the logic lives here once.
 */
export const useClientSearch = (scope: TClientScope = "all", pageSize = DEFAULT_PAGE_SIZE): UseClientSearch => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<TClientForm[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const seqRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runSearch = useCallback(
    (raw: string) => {
      const q = raw.trim();
      if (q.length > 0 && q.length < MIN_NAME_CHARS && !looksNumericOrDate(q)) {
        setResults([]);
        setLoading(false);
        setError(false);
        return;
      }
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const seq = ++seqRef.current;
      setLoading(true);
      setError(false);
      getClientsPaged({ search: q, scope, page_size: pageSize }, controller.signal)
        .then((res) => {
          if (seq !== seqRef.current) return; // superseded by a newer query — drop
          setResults(res.data.results || []);
          setLoading(false);
        })
        .catch(() => {
          if (controller.signal.aborted || seq !== seqRef.current) return; // cancelled — ignore
          setError(true);
          setLoading(false);
        });
    },
    [scope, pageSize],
  );

  const onSearch = useCallback(
    (raw: string) => {
      setQuery(raw);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => runSearch(raw), SEARCH_DEBOUNCE_MS);
    },
    [runSearch],
  );

  const reset = useCallback(() => {
    setQuery("");
    setResults([]);
    setLoading(false);
    setError(false);
    abortRef.current?.abort();
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  useEffect(
    () => () => {
      abortRef.current?.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const trimmed = query.trim();
  const belowMinChars = trimmed.length > 0 && trimmed.length < MIN_NAME_CHARS && !looksNumericOrDate(trimmed);

  return {
    query,
    results,
    loading,
    error,
    belowMinChars,
    onSearch,
    retry: () => runSearch(query),
    reset,
  };
};

export default useClientSearch;
